import React from 'react';
import { formatDistance, calculateDistance } from '../../../services/location.service';

/**
 * LocationWarning - Shows warning when technician is far from property
 * @param {Object} props
 * @param {Object} props.currentLocation - {latitude, longitude, accuracy}
 * @param {Object} props.propertyLocation - {latitude, longitude}
 * @param {number} props.thresholdMeters - Distance threshold in meters (default: 75)
 * @param {Function} props.onContinue - Callback when user chooses to continue anyway
 * @param {Function} props.onCancel - Callback when user cancels
 */
function LocationWarning({ currentLocation, propertyLocation, thresholdMeters = 75, onContinue, onCancel }) {
  if (!currentLocation || !propertyLocation?.latitude || !propertyLocation?.longitude) {
    return null;
  }

  const distance = calculateDistance(
    currentLocation.latitude,
    currentLocation.longitude,
    propertyLocation.latitude,
    propertyLocation.longitude
  );

  const isFar = distance > thresholdMeters;

  if (!isFar) {
    return null;
  }

  return (
    <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">You are far from the property location</h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              You are approximately <strong>{formatDistance(distance)}</strong> away from the property.
              The expected distance is within {thresholdMeters}m.
            </p>
            <p className="mt-2">
              If you need to start work from a different location (e.g., hardware store), you can continue anyway.
              This location will be logged for admin review.
            </p>
          </div>
          <div className="mt-4 flex space-x-3">
            <button
              onClick={onContinue}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              Continue Anyway
            </button>
            <button
              onClick={onCancel}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationWarning;
